import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import type { ContributorData } from './hooks/useProcessedContributorsResult.ts';
import { ContributorItem } from './ContributorItem.tsx';
import { ShowMoreButton } from './ShowMoreButton.tsx';

const styles = cssMap({
	listSeparator: {
		// Preserve locale-provided whitespace (e.g. the space in ", ") inside the flex container.
		whiteSpace: 'pre',
	},
});

export interface FlatLayoutProps {
	visibleContributors: ContributorData[];
	hiddenContributors: ContributorData[];
	showCount: boolean;
	showTime: boolean;
}

export const FlatLayout = ({
	visibleContributors,
	hiddenContributors,
	showCount,
	showTime,
}: FlatLayoutProps): React.JSX.Element => {
	const [showHidden, setShowHidden] = useState(false);
	const intl = useIntl();

	const handleShowMore = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			setShowHidden(true);
		},
		[setShowHidden],
	);

	const renderContributor = (contributor: ContributorData) => (
		<ContributorItem
			key={contributor.id}
			contributor={contributor}
			showCount={showCount}
			showTime={showTime}
		/>
	);

	const listItems: React.JSX.Element[] = visibleContributors.map(renderContributor);

	if (hiddenContributors.length > 0) {
		if (showHidden) {
			listItems.push(...hiddenContributors.map(renderContributor));
		} else {
			listItems.push(
				<ShowMoreButton
					key="contributors-show-more"
					hiddenCount={hiddenContributors.length}
					onClick={handleShowMore}
				/>,
			);
		}
	}

	// Separators between items come from the locale's CLDR list patterns (Intl.ListFormat)
	// rather than a standalone translatable string, so each locale gets correct punctuation.
	const parts = intl.formatListToParts(listItems, { type: 'unit' });

	return (
		<Inline as="span" testId="contributors-flat-layout" alignBlock="baseline" shouldWrap>
			{parts.map((part, index) =>
				part.type === 'literal' ? (
					<Box as="span" key={`separator-${index}`} xcss={styles.listSeparator}>
						{part.value}
					</Box>
				) : (
					part.value
				),
			)}
		</Inline>
	);
};
